import React from 'react';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCampaignMutations, useFetchCampaigns } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignStatus, statusLabel, uniqueKey } from '../../../../support/campaigns';
import {
  BuilderRoot,
  Card,
  GhostButton,
  PrimaryButton,
  SectionHeader,
  StatusPill,
  StatusTone,
  builderStyles,
} from './primitives';
import CampaignMetaForm from './CampaignMetaForm';
import AiOutlineButton from './ai/AiOutlineButton';
import { persistStubTree } from './persistSteps';
import { CampaignOutlineDraft } from '../ai/campaignFlows';

/**
 * The Campaign Builder landing: a card grid of every campaign template, plus two ways to start a new
 * one — a blank record form or an AI-drafted outline (name + description + a whole step tree) that is
 * persisted in one go and then opened for editing.
 *
 * Selecting or creating a campaign calls `onOpen(id)`; the parent (CampaignBuilder) swaps to the
 * editor shell. Writes reuse `useCampaignMutations`; the AI outline persists its step tree through
 * `persistStubTree` (full-record POSTs, parent-before-children).
 */

const CAMPAIGN_TONE: Record<CampaignStatus, StatusTone> = {
  draft: 'idle',
  published: 'done',
  archived: 'warn',
};

const campaignTone = (status?: string): StatusTone =>
  CAMPAIGN_TONE[(status as CampaignStatus)] ?? 'idle';

export function CampaignBuilderList(props: {
  canWrite: boolean;
  onOpen: (id: number) => void;
}): JSX.Element {
  const { canWrite, onOpen } = props;
  const { data: campaigns = [], isLoading } = useFetchCampaigns();
  const { create } = useCampaignMutations('campaign');
  const stepMutations = useCampaignMutations('step');

  const [mode, setMode] = React.useState<'idle' | 'blank' | 'ai'>('idle');
  const [seed, setSeed] = React.useState('');
  const [persisting, setPersisting] = React.useState(false);

  const takenKeys = React.useMemo(() => campaigns.map((c) => c.key), [campaigns]);

  // Persist an AI outline: create the campaign record, then walk its proposed step tree.
  const acceptOutline = React.useCallback(
    async (draft: CampaignOutlineDraft) => {
      setPersisting(true);
      try {
        const key = uniqueKey(draft.name || seed || 'campaign', takenKeys);
        const row = (await create.mutateAsync({
          key,
          slug: key,
          version: 1,
          name: (draft.name || seed || 'Untitled campaign').trim(),
          description: draft.description?.trim() || null,
          image_url: null,
          status: 'draft',
          owner: null,
          estimated_duration_minutes: draft.estimated_duration_minutes ?? null,
          audience_rules: {},
          metadata: {},
          is_active: true,
          available_from: null,
          available_to: null,
        })) as Campaign;
        if (row?.id != null) {
          await persistStubTree(stepMutations.create.mutateAsync, row.id, draft.steps || [], null, 0);
          onOpen(row.id);
        }
      } finally {
        setPersisting(false);
        setMode('idle');
        setSeed('');
      }
    },
    [create, seed, stepMutations.create.mutateAsync, takenKeys, onOpen]
  );

  return (
    <BuilderRoot>
      <SectionHeader
        title="Campaign Builder"
        subtitle="Create and manage the steps that make up each enablement & delivery campaign."
        actions={
          canWrite ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <GhostButton onClick={() => setMode(mode === 'ai' ? 'idle' : 'ai')}>
                ✨ Start with AI
              </GhostButton>
              <PrimaryButton onClick={() => setMode(mode === 'blank' ? 'idle' : 'blank')}>
                <AddIcon sx={{ fontSize: 16 }} />
                New campaign
              </PrimaryButton>
            </div>
          ) : undefined
        }
      />

      {canWrite && mode === 'ai' && (
        <Card className={builderStyles.contentCard}>
          <SectionHeader
            title="Start with AI"
            subtitle="Describe the campaign in a sentence; the Guide drafts a full outline you can review before it is created."
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className={builderStyles.aiSeedInput}
              style={{
                flex: '1 1 320px',
                minWidth: 240,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--cb-border, #e5e7eb)',
                font: 'inherit',
              }}
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="e.g. Zero-trust rollout for mid-market customers"
              aria-label="Campaign idea"
            />
            <AiOutlineButton
              name={seed}
              description={seed}
              disabled={!seed.trim() || persisting}
              onAccept={acceptOutline}
            />
            <GhostButton onClick={() => setMode('idle')} disabled={persisting}>
              Cancel
            </GhostButton>
          </div>
          {persisting && (
            <p className={builderStyles.lead} style={{ marginTop: 12 }}>
              <CircularProgress size={14} /> Creating campaign and steps…
            </p>
          )}
        </Card>
      )}

      {canWrite && mode === 'blank' && (
        <CampaignMetaForm
          takenKeys={takenKeys}
          canWrite={canWrite}
          onDone={() => setMode('idle')}
          onCreated={(id) => onOpen(id)}
        />
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      ) : campaigns.length === 0 ? (
        <Card className={builderStyles.contentCard}>
          <p className={builderStyles.lead}>
            No campaigns yet. {canWrite ? 'Create one to start building its steps.' : ''}
          </p>
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
            marginTop: 16,
          }}
        >
          {campaigns.map((c) => (
            <Card key={c.id} className={builderStyles.campaignCard} onClick={() => onOpen(c.id)}>
              <div className={builderStyles.campaignCardHead}>
                <h4 className={builderStyles.blockTitle} style={{ margin: 0 }}>
                  {c.name}
                </h4>
                <StatusPill tone={campaignTone(c.status)}>{statusLabel(c.status)}</StatusPill>
              </div>
              {c.description && (
                <p
                  className={builderStyles.lead}
                  style={{
                    margin: 0,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {c.description}
                </p>
              )}
              <div className={builderStyles.campaignCardMeta}>
                {c.owner && <span>{c.owner}</span>}
                {c.estimated_duration_minutes != null && (
                  <span>{c.estimated_duration_minutes} min</span>
                )}
                {!c.is_active && <span>Inactive</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </BuilderRoot>
  );
}

export default CampaignBuilderList;
